import React from 'react';

import {RegistryLabelingStrategy, SequenceSchemaType} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import GenericNameTemplateField from 'name-templates/GenericNameTemplateField';
import {NameTemplatePart, NameTemplateType} from 'name-templates/NameTemplateConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import LabelingStrategyWarning from 'registry/labeling-strategies/LabelingStrategyWarning';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import runModal from 'ui-library/runModal';
import {SettingsPopover} from 'ui-library/SettingsField';

import SetNameTemplateModal from './SetNameTemplateModal';

export interface EntityNameTemplateFieldProps {
  bioentityType: string; // see `client/constants/ModelTypeConstants.ts`
  sequenceType: SequenceSchemaType | null; // relevant when bioentityType = SEQUENCE
  schemaFields: Array<SchemaField | ClientSchemaField>;
  schemaId: string;
  nameTemplateParts: Array<NameTemplatePart>;
  shouldOrderNamePartsBySequence: boolean;
  isWritable: boolean; // whether we allow changing the name template
  setNameTemplateParts: (parts: Array<NameTemplatePart>, shouldOrderNamePartsBySequence: boolean) => void;
  labelingStrategies: Array<RegistryLabelingStrategy>;
  nameTemplatesOverrideSource?: SchemaInterfaceSummary | null;
  itemType: TagSchemaItemType | null;
}

export default class EntityNameTemplateField extends React.Component<EntityNameTemplateFieldProps> {
  handleSetNameTemplate = async (): Promise<void> => {
    await runModal(SetNameTemplateModal, {
      bioentityType: this.props.bioentityType,
      schemaFields: this.props.schemaFields,
      schemaId: this.props.schemaId,
      sequenceType: this.props.sequenceType,
      initialNameTemplateParts: this.props.nameTemplateParts,
      initialShouldOrderNamePartsBySequence: this.props.shouldOrderNamePartsBySequence,
      nameTemplateType: NameTemplateType.ENTITY,
      itemType: this.props.itemType,
      onRequestSubmit: (submitArgs) => {
        this.props.setNameTemplateParts(
          submitArgs.nameTemplateParts,
          submitArgs.shouldOrderNamePartsBySequence!
        );
      },
    });
  };

  handleRemoveNameTemplate = (): void => {
    this.props.setNameTemplateParts([], false);
  };

  render(): JSX.Element {
    const helpIconContent = (
      <SettingsPopover
        title="Name template"
        renderContent={() => (
          <div className="tagSchemaSettings-popover">
            <p>
              Automatically rename your entities according to this template. This is a point-in-time renaming
              that only happens during the act of entity registration.
            </p>
            <p>
              Existing registered entities will not have their names automatically updated in response to
              changes in entity metadata or the name template.
            </p>
          </div>
        )}
      />
    );

    return (
      <GenericNameTemplateField
        {...this.props}
        setNameTemplateParts={this.handleSetNameTemplate}
        removeNameTemplateParts={this.handleRemoveNameTemplate}
        helpIconContent={helpIconContent}
        warning={
          <LabelingStrategyWarning
            labelingStrategies={this.props.labelingStrategies}
            nameTemplateParts={this.props.nameTemplateParts}
          />
        }
        testAttrComponent="EntityNameTemplateField"
        nameTemplatesOverrideSource={this.props.nameTemplatesOverrideSource}
      />
    );
  }
}
